import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, firstValueFrom, map } from 'rxjs';
import { ApiGatewayService } from './api-gateway.service';
import { ClientVersionService } from './client-version.service';
import { LocalStorageService } from './local-storage.service';
import { ServiceLocationService } from './service-location.service';
import { User } from '../models/user.model';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly apiGatewayService = inject(ApiGatewayService);
  private readonly clientVersionService = inject(ClientVersionService);
  private readonly localStorageService = inject(LocalStorageService);
  private readonly serviceLocationsService = inject(ServiceLocationService);

  private userSubject = new BehaviorSubject<User | null>(null);
  user$ = this.userSubject.asObservable();

  isLoggedIn$ = this.user$.pipe(map((user) => !!user));
  isLoggedOut$ = this.isLoggedIn$.pipe(map((isLoggedIn) => !isLoggedIn));

  private isReadySubject = new BehaviorSubject(false);
  isReady$ = this.isReadySubject.asObservable();

  constructor() {
    Promise.resolve()
      .then(() => this.localStorageService.getUser())
      .then((user) => this.userSubject.next(user))
      .catch((error) => console.error('error retrieving user from local storage:', error))
      .finally(() => this.isReadySubject.next(true));
  }

  async loginAsync(username: string, password: string): Promise<User> {
    const user$ = this.apiGatewayService.post<User>('oss/accounts/signin', '1.0.0', {
      username,
      password,
    });
    const user = await firstValueFrom(user$);
    this.localStorageService.setUser(user);
    this.userSubject.next(user);
    return user;
  }

  async logoutAsync(): Promise<void> {
    const logout$ = this.apiGatewayService.post('common/accounts/signout', '1.0.0', {});
    await firstValueFrom(logout$);
    await this.resetState();
  }

  async checkTokenAsync(): Promise<{ success: boolean }> {
    const checkToken$ = this.apiGatewayService.get<{ success: boolean }>(
      'common/accounts/checkToken',
      '1.0.0',
      {},
    );
    return await firstValueFrom(checkToken$);
  }

  resetState(): void {
    this.clearUser();
    this.clientVersionService.clearClientVersion();
    this.serviceLocationsService.clearServiceLocations();
    this.localStorageService.clearUser();
    this.localStorageService.clearClientVersion();
    this.localStorageService.clearServiceLocations();
  }

  private clearUser(): void {
    this.userSubject.next(null);
  }
}
